import React, { useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  Body,
  Button,
  Grid,
  Modal,
  Radio,
  Spinner
} from '@walmart-web/livingdesign-components';

import styles from './styles.module.css';
import { useAppSelector } from '../../../../../redux/hooks.ts';
import { ihutSelectors } from '../../../../../redux/slices/ihut.ts';
import Skeleton from '../../../../../components/Skeleton';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (val: boolean) => void;
};

const ADDITIONAL_PRODUCT_WAIT_DAYS = 2;

type ProductRecieved = 'yes' | 'no' | '';

const SupportInProgressModal: React.FC<Props> = ({
  isOpen,
  handleClose,
  handleSubmit
}) => {
  const { t } = useTranslation('ihut');
  const [isProductReceived, setIsProductReceived] =
    useState<ProductRecieved>('');

  const supportRequest = useAppSelector(ihutSelectors.selectSupportRequest);
  const supportLoading = useAppSelector(ihutSelectors.selectSupportLoading);
  const updateStateLoading = useAppSelector(
    ihutSelectors.selectUpdateStateLoading
  );

  const supportRequestedDate = moment(supportRequest?.createdAt);
  const presentDate = moment();
  const diffInDays = moment
    .duration(presentDate.diff(supportRequestedDate))
    .asDays();
  const showRadioOptions = diffInDays >= ADDITIONAL_PRODUCT_WAIT_DAYS;

  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsProductReceived(e.target.value as ProductRecieved);
  };

  const handleModalClose = () => {
    if (updateStateLoading) return;
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={t('support.inProgress')}
      actions={
        showRadioOptions &&
        !supportLoading && (
          <Button
            variant="primary"
            size="medium"
            isFullWidth
            disabled={!isProductReceived || updateStateLoading}
            onClick={() => handleSubmit(isProductReceived === 'yes')}
          >
            {updateStateLoading ? (
              <Spinner size="small" />
            ) : (
              t('submit', { ns: 'common' })
            )}
          </Button>
        )
      }
    >
      {supportLoading ? (
        <Skeleton count={5} />
      ) : (
        <>
          <Body UNSAFE_style={{ whiteSpace: 'pre-line' }} size="large">
            {t('support.inProgressModalMessage')}
            {showRadioOptions
              ? t('support.inSupportModalShowSelectNow')
              : t('support.inSupportModalSelectLater')}
          </Body>

          {showRadioOptions && (
            <Grid UNSAFE_className={styles.optionsContainer}>
              <Body size="small">{t('support.selectOption')}</Body>

              <Radio
                checked={isProductReceived === 'yes'}
                name="productReceived"
                label={t('support.gotProduct')}
                onChange={handleOptionChange}
                value="yes"
              />
              <Radio
                checked={isProductReceived === 'no'}
                name="productReceived"
                label={t('support.neverGotProduct')}
                onChange={handleOptionChange}
                value="no"
              />
            </Grid>
          )}
        </>
      )}
    </Modal>
  );
};

export default SupportInProgressModal;
